import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  placeholder = "usuario@exemplo.com",
  label,
  required = false,
  disabled = false,
  error
}) => {
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Função para validar email
  const validateEmail = (email: string): { isValid: boolean; message: string } => {
    if (!email) {
      return { isValid: !required, message: required ? 'Email é obrigatório' : '' };
    }

    // Verificar se tem @
    if (!email.includes('@')) {
      return { isValid: false, message: 'Email deve conter @' };
    }

    // Verificar se não tem espaços
    if (email.includes(' ')) {
      return { isValid: false, message: 'Email não pode conter espaços' };
    }

    // Verificar se não tem caracteres duplicados problemáticos
    if (email.includes('..')) {
      return { isValid: false, message: 'Email não pode ter pontos consecutivos' };
    }

    // Verificar tamanho
    if (email.length > 254) {
      return { isValid: false, message: 'Email muito longo' };
    }

    // Verificar domínio completo
    const parts = email.split('@');
    if (parts.length !== 2) {
      return { isValid: false, message: 'Email deve ter apenas um @' };
    }

    const [localPart, domain] = parts;
    
    // Validar parte local (antes do @)
    if (!localPart || localPart.length < 1) {
      return { isValid: false, message: 'Email deve ter nome antes do @' };
    }

    // Validar domínio (depois do @)
    if (!domain || domain.length < 3) {
      return { isValid: false, message: 'Domínio incompleto' };
    }

    // Verificar se o domínio tem pelo menos um ponto
    if (!domain.includes('.')) {
      return { isValid: false, message: 'Domínio deve ter extensão (ex: .com, .br)' };
    }

    // Verificar se a extensão tem pelo menos 2 caracteres
    const domainParts = domain.split('.');
    const extension = domainParts[domainParts.length - 1];
    if (!extension || extension.length < 2) {
      return { isValid: false, message: 'Extensão do domínio inválida' };
    }

    // Verificar se não termina com ponto
    if (domain.endsWith('.')) {
      return { isValid: false, message: 'Email não pode terminar com ponto' };
    }

    // Regex completa para validação final
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Formato de email inválido' };
    }

    return { isValid: true, message: '' };
  };

  // Validar em tempo real
  useEffect(() => {
    if (value) {
      const validation = validateEmail(value);
      setIsValid(validation.isValid);
      setErrorMessage(validation.message);
    } else {
      setIsValid(true);
      setErrorMessage('');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();
    onChange(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Não permitir espaços
    if (e.key === ' ') {
      e.preventDefault();
    }
  };

  const getBorderColor = () => {
    if (error || !isValid) return '#dc2626';
    if (isFocused) return '#3b82f6';
    return '#d1d5db';
  };

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '5px',
          fontWeight: 'bold',
          color: '#374151'
        }}>
          {label}
          {required && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          type="email"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '12px 40px 12px 12px',
            border: `2px solid ${getBorderColor()}`,
            borderRadius: '6px',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: disabled ? '#f9fafb' : 'white',
            transition: 'border-color 0.2s'
          }}
        />
        
        <div style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Mail style={{ fontSize: '16px' }} />
        </div>
      </div>
      
      {(error || !isValid) && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>⚠️</span>
          {error || errorMessage}
        </div>
      )}
      
      {isValid && value && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#059669',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>✅</span>
          Email válido
        </div>
      )}
      
      <div style={{
        marginTop: '4px',
        fontSize: '11px',
        color: '#6b7280'
      }}>
        💡 Digite um email válido (exemplo: usuario@dominio.com)
      </div>
    </div>
  );
};

export default EmailInput;
